import React, { useEffect } from 'react';
import { Switch, useLocation, Redirect } from 'react-router-dom';

import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga4';

import Home from 'containers/Home';
import authRoutes from 'components/Authentication';
import ProtectedRoute from 'components/ProtectedRoute';
import ProviderSearch from 'containers/ProviderSearch';
import Provider from 'containers/Provider';
import Family from 'containers/Family';
import { breakpointsContext } from 'utils';
import useBreakpoints from 'hooks/useBreakpoints';
import Admin from 'containers/Admin';
import Internal from 'containers/Internal';
import { AUTH_STATES, ROLES } from 'constants/index';
import { selectors, selectRoles } from 'features/auth';
import Loading from 'components/Loading';
import PublicSearch from 'containers/PublicSearch';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ROLES_WITH_ACCESS_TO_USERS_PAGE } from 'containers/Admin/Users/Users';
import { useFlags } from 'launchdarkly-react-client-sdk';

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const breakpoints = useBreakpoints();
  const isLoaded = useSelector(selectors.selectAuthLoaded);
  const authState = useSelector(selectors.selectAuthState);
  const user = useSelector(selectors.selectUser);
  const activeRole = useSelector(selectors.selectActiveRole);
  const roles = useSelector(selectRoles);

  const { scheduledMaintenance } = useFlags();

  useEffect(() => {
    // Reporting user ID to New Relic
    // @ts-ignore
    if (typeof window !== 'undefined' && window?.newrelic && user?.username) {
      console.log('report', user.username);

      // @ts-ignore
      window?.newrelic?.setUserId?.(user.username);
    }
  }, [user]);

  useEffect(() => {
    message.config({
      top: 100,
      duration: 3,
      rtl: false,
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    newrelic.setCurrentRouteName(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    dispatch({ type: 'APP_LOADED' });
  }, [dispatch]);

  // Google Analytics
  useEffect(() => {
    if (process.env.REACT_APP_ENV !== 'test') {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }
  }, [location.pathname, location.search]);

  if (!isLoaded) return <Loading />;

  return (
    <HelmetProvider>
      <Helmet>
        <title>Great Child Care</title>
      </Helmet>

      {scheduledMaintenance?.enabled && (
        <div className="block w-full bg-orange-100 p-4 box-border leading-6 text-center">
          Heads up! We’re performing scheduled maintenance on{' '}
          <span className="font-semibold">{scheduledMaintenance?.dateTime || '-'}</span>. The site will be temporarily
          unavailable. Thanks for your patience!
        </div>
      )}

      <breakpointsContext.Provider value={breakpoints}>
        <Switch>
          <Redirect from="/:url*(/+)" to={location.pathname.slice(0, -1)} />

          <ProtectedRoute exact path="/" message="Navigated to Home Page">
            <Home />
          </ProtectedRoute>

          <ProtectedRoute exact path="/search" message="Navigated to Provider Search Page">
            <PublicSearch />
          </ProtectedRoute>

          {activeRole !== ROLES.PROVIDER && activeRole !== ROLES.FAMILY && (
            <ProtectedRoute
              path={['/search', '/families', '/providers', '/dashboard', '/test', '/account']}
              hideFor={[ROLES.FAMILY, ROLES.PROVIDER]}
              message="Navigated to Dashboard"
              authRequired
            >
              <Internal />
            </ProtectedRoute>
          )}

          {ROLES_WITH_ACCESS_TO_USERS_PAGE.filter((role) => roles.includes(role)).map((role) => (
            <ProtectedRoute
              key={`admin-users-route-${role}`}
              path={[
                '/admin/users',
                '/admin/users/internal',
                '/admin/users/providers',
                '/admin/users/families',
                '/admin/add-user',
                '/admin/update-user/',
                '/account',
                '/dashboard',
              ]}
              roleRequired={[role]}
              message="Navigated to Users page"
              authRequired
            >
              <Admin />
            </ProtectedRoute>
          ))}

          <ProtectedRoute exact path="/provider-search" message="Navigated to Provider Search Page">
            <ProviderSearch />
          </ProtectedRoute>

          {authRoutes}

          {activeRole === ROLES.PROVIDER && (
            <ProtectedRoute
              authRequired
              path={['/provider', '/account', '/dashboard']}
              roleRequired={[ROLES.PROVIDER]}
              message=""
            >
              <Provider />
            </ProtectedRoute>
          )}

          {activeRole === ROLES.FAMILY && (
            <ProtectedRoute
              path={['/family', '/account', '/dashboard']}
              authRequired
              roleRequired={[ROLES.FAMILY]}
              message="Family Page"
            >
              <Family />
            </ProtectedRoute>
          )}

          {authState === AUTH_STATES.SIGN_IN ? (
            <Redirect
              to={
                location.pathname.startsWith('/family')
                  ? `/family/signin?next=${encodeURIComponent(location.pathname + location.search)}`
                  : `/signin?next=${encodeURIComponent(location.pathname + location.search)}`
              }
            />
          ) : (
            <Redirect to="/" />
          )}
        </Switch>
      </breakpointsContext.Provider>
    </HelmetProvider>
  );
}

export default App;
